/**
 * API client — thin fetch wrapper around the /api/v1 endpoints.
 * All responses use the house envelope { success, data, meta } (see api/src/shared/response.ts).
 */

export interface ApiError {
  code: string;
  message: string;
  details?: Array<{ field: string; message: string }>;
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
  meta?: { requestId: string; timestamp: string };
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: ApiError | undefined,
    public readonly requestId: string | undefined
  ) {
    super(error?.message ?? `API request failed (${status})`);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  const body = (await res.json().catch(() => null)) as Envelope<T> | null;

  if (!res.ok || !body?.success) {
    throw new ApiRequestError(res.status, body?.error, body?.meta?.requestId);
  }

  return body.data as T;
}

/**
 * Mirrors PaginationInfo in api/src/shared/response.ts.
 *
 * The field is `limit`, not `pageSize` — corrected in P30, when the ticket list
 * became the second caller. The old name typechecked and was simply never read;
 * `total` was the only field anything used.
 */
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * A paged request, keeping the pagination block.
 *
 * `request<T>()` returns `body.data` and drops everything beside it — but
 * `response.ts` emits `pagination` as a SIBLING of `data`, not inside it. So a
 * list endpoint's page count was reaching the browser and being thrown away,
 * which is why nothing in the SPA can paginate today. Anything that needs the
 * total goes through here instead.
 */
async function requestPaged<T>(path: string, init?: RequestInit): Promise<{ data: T; pagination?: PaginationInfo }> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });

  const body = (await res.json().catch(() => null)) as (Envelope<T> & { pagination?: PaginationInfo }) | null;

  if (!res.ok || !body?.success) {
    throw new ApiRequestError(res.status, body?.error, body?.meta?.requestId);
  }

  return { data: body.data as T, pagination: body.pagination };
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  getPaged: <T>(path: string) => requestPaged<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) }),
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PUT', body: data === undefined ? undefined : JSON.stringify(data) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
